import { useState } from "react";
import * as Sentry from "@sentry/react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { fill } from "@/lib/i18n/fill";
import type {
  AddFileToFolderConfirmation,
  AddPMCommentConfirmation,
  ChatChangeConfirmation,
  CreatePMIssueConfirmation,
  SetDocumentVerifiedConfirmation,
  ToolConfirmationPayload,
  UpdateBuildingDataConfirmation,
  UpdatePMIssueConfirmation,
} from "@/stores/chat/repository";
import { PMIssueConfirmationView } from "./PMIssueConfirmationView";
import { AddFileToFolderConfirmationView } from "./AddFileToFolderConfirmationView";
import type { ChatTextSize } from "./types";
import { ActionDetails } from "@/components/connectors/ActionDetails";

const KNOWN_TOOL_NAMES = [
  "update_building_data",
  "create_pm_issue",
  "update_pm_issue",
  "add_pm_issue_comment",
  "add_file_to_folder",
  "set_document_verified",
] as const;

interface BuildingDataConfirmationCardProps {
  payload: ToolConfirmationPayload;
  /**
   * Called with `{ remember, overrides }` so the caller can persist the
   * auto-approve preference and forward any tool-arg overrides the user
   * adjusted inside the card (WHA-3354 — e.g. switching the target folder
   * for `add_file_to_folder`). Backwards-compatible — both fields are
   * optional and default to undefined.
   */
  onApprove: (options?: {
    remember?: boolean;
    overrides?: Record<string, unknown>;
  }) => void | Promise<void>;
  onDeny: () => void | Promise<void>;
  /** When set, shows a finalized state without action buttons. */
  resolved?: "approved" | "denied" | null;
  /** How many confirmations this thread is still holding, this one included.
   *  One turn raises a card per write-tool call — twelve of them for "create
   *  tasks from this protocol" — so a lone card would read as the whole
   *  change. Not an "N of M" position: answered cards leave the queue, so the
   *  card on screen is always the first one left. */
  count?: number;
  /** Give up on a card: the expired state, where approve and deny have nothing
   *  left to answer, and any failed submit, whose retry may never land. */
  onDismiss?: () => void;
  /** In-flight / failed state for this card's submission. The card stays up
   *  through both: clearing it on click and swallowing the error left the user
   *  with a spinner and no way back. */
  submission?: {
    /** Which button the failed/in-flight submit belongs to, so "Try again"
     *  lands on the action the user actually pressed. */
    action?: "approve" | "deny";
    state: "submitting" | "failed";
    errorKey?: "submitFailed" | "expired";
  } | null;
  textSize?: ChatTextSize;
}

/**
 * Inline confirmation card rendered inside a Franklin assistant message.
 * Dispatches to a tool-specific sub-view based on `payload.tool_name`:
 *
 *   - update_building_data → attribute diff (was: this card's only mode)
 *   - create_pm_issue / update_pm_issue / add_pm_issue_comment → PM views
 *
 * Owns the Approve / Deny / "Always allow" affordances so every tool
 * shares the same interaction. Component name is unchanged for now so
 * existing imports keep working.
 */
export function BuildingDataConfirmationCard({
  payload,
  onApprove,
  onDeny,
  resolved,
  count = 1,
  onDismiss,
  submission = null,
  textSize = "sm",
}: BuildingDataConfirmationCardProps) {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState<"approve" | "deny" | null>(null);
  const [remember, setRemember] = useState(false);
  // WHA-3354: tool-arg overrides the user adjusted inside the card (e.g.
  // switched the target folder for add_file_to_folder). Most tools never
  // populate this; the sub-view calls back via `onOverridesChange`.
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});
  const textSizeClass = textSize === "lg" ? "text-[15px]" : "text-[14px]";

  const finalized = Boolean(resolved);
  // A `reply`-scoped tick saves nothing, so it needs no Settings toggle to
  // revoke it and is safe to offer on any tool the chatbot names.
  const replyOnly = payload.always_allow_scope === "reply";
  const canRemember =
    (replyOnly && Boolean(payload.tool_name)) ||
    KNOWN_TOOL_NAMES.some((name) => name === payload.tool_name);
  // The backend is no longer waiting on this question, so Approve, Deny and
  // Try again would all be buttons whose only outcome is another 404. The card
  // stays up to say so, and dismissing it is the only thing left to do.
  const expired = submission?.state === "failed" && submission.errorKey === "expired";
  const copy = t.documents.chatSidebar;
  // The store owns the submit; a failure leaves the card up so the buttons
  // have to come back, hence `submitting` alone cannot gate them.
  const busy = submitting !== null || submission?.state === "submitting";
  // The retry belongs to the button that failed. Labelling Approve "Try again"
  // after a failed Deny puts the retry affordance on the button that performs
  // the write — one mis-click and the tool call the user rejected goes through.
  // An older submission carrying no action falls back to Approve, which is what
  // it always meant.
  const failedAction = submission?.state === "failed" ? (submission.action ?? "approve") : null;

  const handleApprove = async () => {
    if (busy || finalized) return;
    setSubmitting("approve");
    try {
      await onApprove({
        remember: canRemember && remember,
        overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
      });
    } finally {
      setSubmitting(null);
    }
  };

  const handleDeny = async () => {
    if (busy || finalized) return;
    setSubmitting("deny");
    try {
      await onDeny();
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div
      className={cn(
        "mt-3 flex flex-col gap-2 rounded-lg border border-neutral-300 bg-neutral-50 p-3",
        textSizeClass
      )}
    >
      {count > 1 ? (
        <div className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          {fill(copy.confirmCounter, { count: String(count) })}
        </div>
      ) : null}

      <ToolConfirmationBody
        payload={payload}
        onOverridesChange={setOverrides}
        finalized={finalized}
      />

      {submission?.state === "failed" ? (
        <div className="text-[11px] text-red-600">
          {submission.errorKey === "expired" ? copy.confirmExpired : copy.confirmSubmitFailed}
        </div>
      ) : null}

      {expired ? (
        <div className="mt-1 flex justify-end">
          <button
            type="button"
            onClick={onDismiss}
            className={cn(
              "rounded-md px-3 py-1 text-xs transition-colors",
              "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
            )}
          >
            {copy.confirmDismiss}
          </button>
        </div>
      ) : finalized ? (
        <div
          className={cn(
            "mt-1 inline-flex w-fit items-center gap-1 rounded-md px-2 py-1 text-xs",
            resolved === "approved"
              ? "bg-primary/10 text-primary"
              : "bg-neutral-200 text-neutral-600"
          )}
        >
          {resolved === "approved" ? (
            <>
              <Check className="h-3 w-3" /> {copy.confirmSaved}
            </>
          ) : (
            <>
              <X className="h-3 w-3" /> {copy.confirmDeclined}
            </>
          )}
        </div>
      ) : (
        <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
          {canRemember && (
            <label
              className="flex select-none items-center gap-1.5 text-[11px] text-neutral-600"
              title={replyOnly ? copy.confirmAllowForReplyHint : undefined}
            >
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                disabled={busy}
                className="h-3 w-3 cursor-pointer accent-primary"
              />
              {replyOnly ? copy.confirmAllowForReply : copy.confirmAlwaysAllow}
            </label>
          )}
          <div className="flex items-center gap-2">
            {/* A retry is not guaranteed to be answerable: once the route is
                unpinned the answer round-robins, and a replica that never held
                the question 404s every time. This card is the head of the
                thread's queue, so leaving "Try again" as the only way out
                blocks every sibling confirmation behind it. */}
            {failedAction ? (
              <button
                type="button"
                onClick={onDismiss}
                disabled={busy}
                className={cn(
                  "rounded-md px-3 py-1 text-xs transition-colors",
                  "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700",
                  "disabled:cursor-default disabled:opacity-60"
                )}
              >
                {copy.confirmDismiss}
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleDeny}
              disabled={busy}
              className={cn(
                "rounded-md px-3 py-1 text-xs transition-colors",
                "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700",
                "disabled:cursor-default disabled:opacity-60"
              )}
            >
              {failedAction === "deny" ? copy.confirmRetry : copy.confirmDeny}
            </button>
            <button
              type="button"
              onClick={handleApprove}
              disabled={busy}
              className={cn(
                "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                "flex items-center gap-1.5",
                "bg-primary text-white hover:bg-primary/90",
                "disabled:cursor-default disabled:opacity-60"
              )}
            >
              <Check className="h-3 w-3" />
              {failedAction === "approve" ? copy.confirmRetry : copy.confirmApprove}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolConfirmationBody({
  payload,
  onOverridesChange,
  finalized,
}: {
  payload: ToolConfirmationPayload;
  onOverridesChange: (overrides: Record<string, unknown>) => void;
  finalized: boolean;
}) {
  const toolName = payload.tool_name;

  if (toolName === "update_building_data") {
    return <UpdateBuildingDataView payload={payload as UpdateBuildingDataConfirmation} />;
  }
  if (toolName === "create_pm_issue") {
    return (
      <PMIssueConfirmationView variant="create" payload={payload as CreatePMIssueConfirmation} />
    );
  }
  if (toolName === "update_pm_issue") {
    return (
      <PMIssueConfirmationView variant="update" payload={payload as UpdatePMIssueConfirmation} />
    );
  }
  if (toolName === "add_pm_issue_comment") {
    return (
      <PMIssueConfirmationView variant="comment" payload={payload as AddPMCommentConfirmation} />
    );
  }
  if (toolName === "add_file_to_folder") {
    return (
      <AddFileToFolderConfirmationView
        payload={payload as AddFileToFolderConfirmation}
        onOverridesChange={onOverridesChange}
        disabled={finalized}
      />
    );
  }
  if (toolName === "set_document_verified") {
    return <SetDocumentVerifiedView payload={payload as SetDocumentVerifiedConfirmation} />;
  }
  if (
    toolName === "update_documents" ||
    toolName === "post_comment" ||
    toolName === "update_contact" ||
    toolName === "update_brief"
  ) {
    return <ChatChangeView payload={payload as ChatChangeConfirmation} />;
  }
  if (
    toolName === "franklin_project_change" ||
    toolName === "franklin_share_change" ||
    toolName === "franklin_document_change" ||
    toolName === "franklin_contact_change" ||
    toolName === "franklin_brief_change" ||
    toolName === "franklin_folder_change" ||
    toolName === "franklin_planning_change" ||
    toolName === "franklin_collaboration_change" ||
    toolName === "franklin_profile_change" ||
    toolName === "franklin_building_change" ||
    toolName === "franklin_member_change" ||
    toolName === "franklin_organization_change"
  ) {
    const { tool_name: _toolName, tool_label: _toolLabel, ...preview } = payload;
    return <ActionDetails value={preview} />;
  }
  return <UnknownToolView payload={payload} />;
}

function ChatChangeView({ payload }: { payload: ChatChangeConfirmation }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="font-semibold text-neutral-800">{payload.summary}</div>
      {payload.details.length > 0 ? (
        <ul className="ml-2 flex list-disc flex-col gap-1 pl-3 text-neutral-700">
          {payload.details.map((line, idx) => (
            <li key={idx} className="break-words">
              {line}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function SetDocumentVerifiedView({ payload }: { payload: SetDocumentVerifiedConfirmation }) {
  // Localised because this card's copy asserts who gets recorded against the
  // document — the titles reuse the same strings as the document row's own
  // verify/unverify actions. The backend stamps who and when, so the card
  // deliberately does not promise a name or a date.
  const { t } = useLanguage();
  return (
    <>
      <div className="font-semibold text-neutral-800">
        {payload.verified ? t.documents.bulkActions.verify : t.documents.bulkActions.unverify}
      </div>
      <div className="break-words text-neutral-700">{payload.filename}</div>
      <div className="text-[12px] text-neutral-500">
        {payload.verified
          ? t.documents.verified.confirmVerifyDescription
          : t.documents.verified.confirmUnverifyDescription}
      </div>
    </>
  );
}

function UnknownToolView({ payload }: { payload: ToolConfirmationPayload }) {
  // Via `unknown`: a discriminated union has no string index signature, so the
  // direct cast is an error. This view exists precisely for payloads that do
  // not match the union, so reading it as a bag of unknowns is the intent.
  const anyPayload = payload as unknown as Record<string, unknown>;
  const toolName =
    typeof anyPayload.tool_name === "string" ? (anyPayload.tool_name as string) : null;
  const toolLabel =
    typeof anyPayload.tool_label === "string" ? (anyPayload.tool_label as string) : null;
  const summary = typeof anyPayload.summary === "string" ? (anyPayload.summary as string) : null;

  if (typeof window !== "undefined") {
    console.warn("[tool_confirmation] Unknown or missing tool_name — rendering fallback card.", {
      toolName,
      payload,
    });
  }
  Sentry.captureMessage("tool_confirmation: unknown tool_name", {
    level: "warning",
    extra: {
      tool_name: toolName,
      known_tool_names: KNOWN_TOOL_NAMES,
      payload,
    },
  });

  return (
    <div className="flex flex-col gap-1.5 text-neutral-700">
      <div className="font-semibold text-neutral-800">{toolLabel ?? "Unknown action"}</div>
      {summary ? <div>{summary}</div> : null}
      <div className="text-[12px] text-neutral-500">
        This version doesn't know how to render this card. Review carefully before approving.
      </div>
    </div>
  );
}

function UpdateBuildingDataView({ payload }: { payload: UpdateBuildingDataConfirmation }) {
  const currentDisplay =
    payload.current_value && payload.current_value.trim().length > 0 ? payload.current_value : "—";
  const unitSuffix = payload.attribute_unit ? ` ${payload.attribute_unit}` : "";
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-neutral-800">{payload.attribute_label}</span>
        {payload.attribute_unit ? (
          <span className="rounded-md border border-neutral-300 bg-white px-1.5 py-0.5 text-[11px] text-neutral-600">
            {payload.attribute_unit}
          </span>
        ) : null}
      </div>

      <div className="flex flex-wrap items-baseline gap-1.5 text-neutral-700">
        <span className="text-neutral-500">Current:</span>
        <span className="font-medium text-neutral-800">
          {currentDisplay}
          {payload.current_value ? unitSuffix : ""}
        </span>
        <span className="px-1 text-neutral-400">→</span>
        <span className="text-neutral-500">Proposed:</span>
        <span className="font-medium text-primary">
          {payload.proposed_value}
          {unitSuffix}
        </span>
      </div>

      {payload.reasoning ? <p className="text-neutral-600">{payload.reasoning}</p> : null}
    </>
  );
}
